/**
 * Why something could not be shown: not found vs. busy (429) vs. failed.
 *
 * A page that cannot load must say so and offer a next step. It must never
 * fall through to its empty state ("No challenges yet", "Become a coach") —
 * telling a user they have nothing when the server only failed or asked them
 * to wait — and never call a 429 "not found".
 *
 * `notFound` is rendered for 404/400/422 when given (a single resource); a
 * list has no "not found", so without it those statuses count as a failure.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { classifyLoadError } from '../utils/loadError';

export interface LoadErrorNoticeProps {
  error: unknown;
  onRetry: () => void;
  /** Sentence for a failure to load, e.g. "Your modules could not be loaded." */
  failedMessage: string;
  /** Rendered for 404/400/422 (a single resource). Omit for lists. */
  notFound?: ReactNode;
  /** data-testid prefix: `${prefix}-busy` / `${prefix}-load-failed`. */
  testIdPrefix: string;
}

export function LoadErrorNotice({ error, onRetry, failedMessage, notFound, testIdPrefix }: LoadErrorNoticeProps) {
  const { kind, retryAfterSeconds } = classifyLoadError(error);
  const [waitLeft, setWaitLeft] = useState<number>(kind === 'rate_limited' ? retryAfterSeconds ?? 0 : 0);
  useEffect(() => {
    setWaitLeft(kind === 'rate_limited' ? retryAfterSeconds ?? 0 : 0);
  }, [kind, retryAfterSeconds, error]);
  useEffect(() => {
    if (waitLeft <= 0) return;
    const t = setTimeout(() => setWaitLeft((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [waitLeft]);

  if (kind === 'not_found' && notFound !== undefined) return <>{notFound}</>;

  return (
    <div className="space-y-2" role="alert" data-testid={kind === 'rate_limited' ? `${testIdPrefix}-busy` : `${testIdPrefix}-load-failed`}>
      <p className="text-gray-700">
        {kind === 'rate_limited'
          ? waitLeft > 0
            ? `BeHeard is busy right now. Please try again in ${waitLeft} second${waitLeft === 1 ? '' : 's'}.`
            : 'BeHeard is busy right now. Please try again shortly.'
          : `${failedMessage} Please try again.`}
      </p>
      <button
        type="button"
        onClick={onRetry}
        disabled={waitLeft > 0}
        className="px-3 py-1.5 text-sm font-medium rounded-lg bg-gray-100 text-gray-800 hover:bg-gray-200 disabled:opacity-50"
      >
        Try again
      </button>
    </div>
  );
}
